const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_TOKEN ||
  'pk.eyJ1IjoiZ2FycmJlYXItMDkiLCJhIjoiY21keGZqYXd2MDJ4MzJsb2xiOWF4MGxjNyJ9.GefZT_BK-11i70G9CuI2TA'

export interface Coordinates {
  latitude: number
  longitude: number
}

/** Resolve a saved street address to coordinates, with session caching. */
export async function geocodeAddress(address: string): Promise<Coordinates | null> {
  const normalized = address.trim().replace(/\s+/g, ' ')
  if (!normalized || !MAPBOX_TOKEN) return null

  const cacheKey = `hh-geocode:${normalized.toLowerCase()}`
  try {
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) return JSON.parse(cached) as Coordinates
  } catch {
    // Storage can be unavailable in privacy modes; geocoding can still proceed.
  }

  try {
    const params = new URLSearchParams({
      access_token: MAPBOX_TOKEN,
      country: 'us',
      limit: '1',
      types: 'address,place,postcode'
    })
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(normalized)}.json?${params}`
    )
    if (!response.ok) return null

    const data = await response.json()
    const center = data?.features?.[0]?.center
    if (!Array.isArray(center) || center.length < 2) return null

    const longitude = Number(center[0])
    const latitude = Number(center[1])
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

    const result = { latitude, longitude }
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result))
    } catch {
      // Ignore cache failures.
    }
    return result
  } catch {
    return null
  }
}

export function formatGeocodableAddress(location: {
  street?: string
  city?: string
  state?: string
  zipCode?: string
}): string {
  return [location.street, location.city, location.state, location.zipCode]
    .filter(Boolean)
    .join(', ')
}
